/**
 * Cascade landing page.
 * Shows cycles with Catchball, per-cycle "your turn" count, and links in.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CircleDot, GitBranch, Inbox, Loader2, RefreshCw, Search } from 'lucide-react';
import { PageHeader } from '../../../../components/prioritization/PageHeader';
import { useDegapProcessStore } from '../../../../stores/prioritization/degapProcessStore';
import { useCatchballLoopStore } from '../../../../stores/prioritization/catchballLoopStore';
import { useWorkflowStore } from '../../../../stores/prioritization/workflowStore';
import { useUserStore } from '../../../../stores/prioritization/userStore';
import { useTrackStore } from '../../../../stores/prioritization/trackStore';
import {
  normalizeLoopState,
  type CatchballLoop,
  type CatchballLoopState,
} from '../../../../types/prioritization/catchballLoop';
/**
 * What is being asked, in the requester's own words: the first ask, else the
 * path this loop runs over, else the first line of strategic context. Only when
 * a loop carries no request at all does the number stand in for a name.
 */
function askLine(loop: CatchballLoop): string {
  const intent = loop.intent;
  const ask = intent?.asks?.find((a) => a.trim().length > 0);
  if (ask) return ask.trim();
  if (intent?.pathName) return intent.pathName;
  const context = intent?.strategicContext?.trim();
  if (context) {
    const firstSentence = context.split(/(?<=[.!?])\s/)[0] ?? context;
    return firstSentence.length > 140 ? `${firstSentence.slice(0, 137)}…` : firstSentence;
  }
  return `Loop ${loop.loopNumber ?? '—'} — no request yet`;
}

/** Plain-English loop state; the raw values are a 20-member legacy union. */
function stateLabel(state: string | undefined): string {
  const s = normalizeLoopState((state ?? 'OPEN') as CatchballLoopState);
  switch (s) {
    case 'PROPOSED':
      return 'Waiting on a response';
    case 'REVISION_REQUESTED':
      return 'Revision requested';
    case 'ACCEPTED':
      return 'Accepted';
    case 'FINAL_REJECTED':
      return 'Rejected';
    case 'DECLINED':
      return 'Closed';
    default:
      return 'Open — your move';
  }
}

function dueLabel(due: string | undefined): { label: string; overdue: boolean } | null {
  if (!due) return null;
  const when = new Date(due);
  if (Number.isNaN(when.getTime())) return null;
  const days = Math.round((when.getTime() - Date.now()) / 86_400_000);
  const date = when.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  if (days < 0) return { label: `Overdue — was due ${date}`, overdue: true };
  if (days === 0) return { label: 'Due today', overdue: true };
  if (days === 1) return { label: 'Due tomorrow', overdue: false };
  if (days <= 14) return { label: `Due in ${days} days`, overdue: false };
  return { label: `Due ${date}`, overdue: false };
}

function isWaitingForReview(state: string | undefined): boolean {
  if (!state) return false;
  const s = state.toUpperCase();
  return ['PROPOSED', 'RETURNED_TO_TOP'].some((x) => s.includes(x));
}

/**
 * Personal inbox filter — holder identity only.
 * Display-only ternary (getBallPossession) is NOT authority here (ADR / Phase B).
 * Org-unit holder truth lives on catchball_active_branch_positions; when a user
 * claim id is present on the loop, that is the inbox signal.
 */
function isInMyCourt(
  ballHolderUserId: string | null | undefined,
  currentUserId: string | null | undefined,
): boolean {
  if (!currentUserId || !ballHolderUserId) return false;
  return ballHolderUserId === currentUserId;
}

export function CatchballExchangeLandingPage() {
  const cyclesRaw = useDegapProcessStore((s) => s.cycles);
  const cycles = useMemo(() => cyclesRaw ?? [], [cyclesRaw]);
  const fetchCycles = useDegapProcessStore((s) => s.fetchFromSupabase);
  const loopsRaw = useCatchballLoopStore((s) => s.loops);
  const loops = useMemo(() => loopsRaw ?? [], [loopsRaw]);
  const fetchLoops = useCatchballLoopStore((s) => s.fetchFromSupabase);
  const getEffectiveConfigForCycle = useWorkflowStore((s) => s.getEffectiveConfigForCycle);
  const userId = useUserStore((s) => s.userId);

  const [catchballEnabledByCycleId, setCatchballEnabledByCycleId] = useState<Record<string, boolean>>({});
  const [search, setSearch] = useState('');

  useEffect(() => {
    void fetchCycles?.();
    void fetchLoops?.();
  }, [fetchCycles, fetchLoops]);

  const activeCycles = useMemo(
    () => cycles.filter((c) => (c as { status?: string }).status !== 'archived'),
    [cycles]
  );

  useEffect(() => {
    if (activeCycles.length === 0) return;
    let cancelled = false;
    Promise.all(
      activeCycles.map((cycle) =>
        getEffectiveConfigForCycle(cycle).then((config) => ({ id: cycle.id, enabled: config.catchballEnabled }))
      )
    ).then((results) => {
      if (!cancelled)
        setCatchballEnabledByCycleId((prev) => ({
          ...prev,
          ...Object.fromEntries(results.map((r) => [r.id, r.enabled])),
        }));
    });
    return () => { cancelled = true; };
  }, [activeCycles, getEffectiveConfigForCycle]);

  const cyclesWithCatchball = useMemo(
    () => activeCycles.filter((c) => catchballEnabledByCycleId[c.id] !== false),
    [activeCycles, catchballEnabledByCycleId]
  );

  // Count per-cycle loops waiting for review
  const yourTurnByCycleId = useMemo(() => {
    const map: Record<string, number> = {};
    loops.forEach((l) => {
      if (isWaitingForReview(l.state)) {
        map[l.cycleId] = (map[l.cycleId] ?? 0) + 1;
      }
    });
    return map;
  }, [loops]);

  const loopCountByCycleId = useMemo(() => {
    const map: Record<string, number> = {};
    loops.forEach((l) => { map[l.cycleId] = (map[l.cycleId] ?? 0) + 1; });
    return map;
  }, [loops]);

  // Personal inbox: loops where the ball is claimed by the current user
  const inMyCourtLoops = useMemo(() => {
    if (!userId) return [];
    return loops.filter((l) => isInMyCourt(l.ballHolderUserId, userId));
  }, [loops, userId]);

  // Track lookup so an inbox row can name the lane it belongs to.
  const tracks = useTrackStore((s) => s.tracks);
  const trackById = useMemo(() => {
    const map: Record<string, { name?: string }> = {};
    (tracks ?? []).forEach((t) => { map[t.id] = t; });
    return map;
  }, [tracks]);

  // Cycle lookup for inbox display
  const cycleById = useMemo(() => {
    const map: Record<string, typeof cycles[number]> = {};
    cycles.forEach((c) => { map[c.id] = c; });
    return map;
  }, [cycles]);

  const displayed = useMemo(() => {
    let list = cyclesWithCatchball;
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((c) => (c.name ?? '').toLowerCase().includes(q));
    }
    return list;
  }, [cyclesWithCatchball, search]);

  return (
    <div className="space-y-5">
      <PageHeader
        icon={<GitBranch className="h-4 w-4 text-[var(--text-ter)]" />}
        iconBg="bg-[var(--slate-dim)]"
        title="Cascade"
        subtitle="Follow alignment requests from the top, capture priorities in sessions, and hand results back up — step by step."
      />

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
        <input
          type="search"
          placeholder="Search cycles…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] pl-10 pr-4 py-2 text-sm"
        />
      </div>

      {/* ── Personal inbox: loops actively waiting for the current user ─── */}
      {inMyCourtLoops.length > 0 && (
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Inbox className="h-4 w-4 text-[var(--text-ter)]" />
            <h2 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)]">
              In your court
            </h2>
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[var(--slate)] text-white text-[0.65rem] font-bold">
              {inMyCourtLoops.length}
            </span>
          </div>
          <ul className="space-y-1.5">
            {inMyCourtLoops.map((loop) => {
              const cycleName = cycleById[loop.cycleId]?.name ?? loop.cycleId;
              const href = `/prioritization/cascade/catchball/${loop.cycleId}/${loop.id}`;
              const ask = askLine(loop);
              const due = dueLabel(loop.intent?.dueDate);
              const trackName = trackById[loop.trackId ?? '']?.name ?? null;
              return (
                <li key={loop.id}>
                  <Link
                    to={href}
                    className="flex items-start justify-between gap-4 border border-[var(--slate)] dark:border-[var(--slate)] bg-[var(--slate-dim)] dark:bg-[var(--slate)]/30 px-4 py-3 hover:bg-[var(--slate-dim)] dark:hover:bg-[var(--slate)]/40 transition-colors group"
                  >
                    <div className="flex items-start gap-3 min-w-0">
                      <div className="p-2 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/50 shrink-0">
                        <CircleDot className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                      </div>
                      <div className="min-w-0">
                        {/* The ask, not the loop number. A row reading "Loop 3"
                            told the holder nothing about what was wanted, so
                            five of them looked identical. */}
                        <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] text-sm line-clamp-2">
                          {ask}
                        </p>
                        <p className="text-xs text-[var(--text-sec)] truncate mt-0.5">
                          {[cycleName, trackName, `Loop ${loop.loopNumber ?? '—'}`]
                            .filter(Boolean)
                            .join(' · ')}
                        </p>
                        <p className="text-xs mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-[var(--text-sec)]">{stateLabel(loop.state)}</span>
                          {due && (
                            <span className={due.overdue ? 'text-[var(--coral)] font-medium' : 'text-[var(--text-sec)]'}>
                              {due.label}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <span className="inline-flex items-center gap-1 text-xs font-medium text-[var(--text-sec)] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                      Open <ArrowRight className="h-3.5 w-3.5" />
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {cyclesWithCatchball.length === 0 && !activeCycles.length ? (
        <div className="flex items-center gap-2 text-sm text-[var(--text-ter)] py-8">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading…
        </div>
      ) : null}

      {cyclesWithCatchball.length === 0 && activeCycles.length > 0 ? (
        <div className="border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] p-10 text-center">
          <RefreshCw className="h-10 w-10 text-[var(--text-ter)] dark:text-[var(--text-dim)] mx-auto mb-4" />
          <h2 className="text-base font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri)] mb-1">
            No cycles with alignment loops enabled
          </h2>
          <p className="text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)] mb-4 max-w-sm mx-auto">
            Assign a workflow with Catchball enabled to a cycle, then come back here.
          </p>
          <Link
            to="/prioritization/cycles"
            className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] text-sm font-medium"
          >
            Go to Cycles
          </Link>
        </div>
      ) : null}

      {displayed.length > 0 ? (
        <ul className="space-y-2">
          {displayed.map((c) => {
            const total = loopCountByCycleId[c.id] ?? 0;
            const yourTurn = yourTurnByCycleId[c.id] ?? 0;
            return (
              <li key={c.id}>
                <Link
                  to={`/prioritization/cascade/${c.id}`}
                  className="flex items-center justify-between gap-4 border border-[var(--rule)] dark:border-[var(--rule)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised)] px-4 py-3.5 hover:border-[var(--slate)] dark:hover:border-[var(--slate)] transition-colors group"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-[var(--slate-dim)] dark:bg-[var(--slate)]/40 shrink-0">
                      <CircleDot className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium text-[var(--text-pri)] dark:text-[var(--text-pri)] truncate">{c.name ?? c.id}</p>
                      <p className="text-xs text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                        {total} loop{total === 1 ? '' : 's'}
                        {yourTurn > 0 && (
                          <span className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40 font-medium">
                            {yourTurn} need{yourTurn === 1 ? 's' : ''} action
                          </span>
                        )}
                      </p>
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
}

export default CatchballExchangeLandingPage;
